import { useEffect, useRef, useState, createContext, useContext, useCallback } from 'react';

/**
 * Form Error Announcer Context
 * 
 * Provides an accessible way to announce form errors to screen readers
 * using aria-live regions (WCAG 2.1 SC 4.1.3: Status Messages)
 * 
 * Usage:
 * 1. Wrap your app/form with <FormErrorProvider>
 * 2. Use the useFormAnnouncer() hook to announce errors
 * 
 * Example:
 * const { announceError, announceSuccess, clearAnnouncement } = useFormAnnouncer();
 * announceError('Please enter a valid email address');
 */

const FormAnnouncerContext = createContext(null);

export function FormErrorProvider({ children }) {
  const [announcement, setAnnouncement] = useState({ message: '', type: 'polite' });
  const announcerRef = useRef(null);
  const timeoutRef = useRef(null);

  // Clear announcement after delay to allow re-announcement of same message
  useEffect(() => {
    if (announcement.message) {
      timeoutRef.current = setTimeout(() => {
        setAnnouncement({ message: '', type: 'polite' });
      }, 5000);
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [announcement]);

  const announce = useCallback((message, type = 'polite') => {
    // Clear first to ensure re-announcement works
    setAnnouncement({ message: '', type });
    // Small delay to ensure screen readers pick up the change
    requestAnimationFrame(() => {
      setAnnouncement({ message, type });
    });
  }, []);

  const announceError = useCallback((message) => {
    announce(message, 'assertive');
  }, [announce]);

  const announceSuccess = useCallback((message) => {
    announce(message, 'polite');
  }, [announce]);

  const announceErrors = useCallback((errors) => {
    if (Array.isArray(errors) && errors.length > 0) {
      const count = errors.length;
      const message = count === 1 
        ? `Error: ${errors[0]}`
        : `${count} errors: ${errors.join('. ')}`;
      announceError(message);
    } else if (typeof errors === 'object' && errors !== null) {
      const errorMessages = Object.values(errors).filter(Boolean);
      if (errorMessages.length > 0) {
        announceErrors(errorMessages);
      }
    }
  }, [announceError]);

  const clearAnnouncement = useCallback(() => {
    setAnnouncement({ message: '', type: 'polite' });
  }, []);

  return (
    <FormAnnouncerContext.Provider value={{ 
      announceError, 
      announceSuccess, 
      announceErrors,
      clearAnnouncement 
    }}>
      {children}
      
      {/* Polite announcements (for success messages, hints) */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {announcement.type === 'polite' && announcement.message}
      </div>
      
      {/* Assertive announcements (for errors) */}
      <div
        ref={announcerRef}
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        className="sr-only"
      >
        {announcement.type === 'assertive' && announcement.message}
      </div>
    </FormAnnouncerContext.Provider>
  );
}

export function useFormAnnouncer() {
  const context = useContext(FormAnnouncerContext);
  if (!context) {
    // Return no-op functions if used outside provider
    return {
      announceError: () => {},
      announceSuccess: () => {},
      announceErrors: () => {},
      clearAnnouncement: () => {},
    };
  }
  return context;
}

/**
 * Inline Error Message Component
 * 
 * Displays error message with proper accessibility attributes
 * 
 * Usage:
 * <FormField>
 *   <Input id="email" aria-describedby="email-error" aria-invalid={!!error} />
 *   <FormError id="email-error" message={error} />
 * </FormField>
 */
export function FormError({ id, message, className = '' }) {
  if (!message) return null;

  return (
    <p 
      id={id}
      role="alert"
      className={`text-sm text-destructive mt-1.5 flex items-center gap-1.5 ${className}`}
    >
      <svg 
        className="h-4 w-4 flex-shrink-0" 
        fill="currentColor" 
        viewBox="0 0 20 20"
        aria-hidden="true"
      >
        <path 
          fillRule="evenodd" 
          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" 
          clipRule="evenodd" 
        />
      </svg>
      <span>{message}</span>
    </p>
  );
}

/**
 * Form Success Message Component
 */
export function FormSuccess({ id, message, className = '' }) {
  if (!message) return null;

  return (
    <p 
      id={id}
      role="status"
      className={`text-sm text-green-700 mt-1.5 flex items-center gap-1.5 ${className}`}
    >
      <svg 
        className="h-4 w-4 flex-shrink-0" 
        fill="currentColor" 
        viewBox="0 0 20 20"
        aria-hidden="true"
      >
        <path 
          fillRule="evenodd" 
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" 
          clipRule="evenodd" 
        />
      </svg>
      <span>{message}</span>
    </p>
  );
}

export default FormErrorProvider;
